import React, { useState, useEffect, useRef } from "react";
import { X, Copy, ExternalLink, Download, AlertTriangle, Check, RefreshCw, Image as ImageIcon, ZoomIn } from "lucide-react";
import { toast } from "sonner";
import type { Leak } from "../types/leak";
import { formatXPost, getVerifiedThreatActor, KNOWN_RANSOMWARE_GROUPS } from "../utils/xShare";
import { fetchLeakById } from "../api/leaks";
import { calculateXPostLength, X_POST_MAX_LIMIT } from "../utils/xValidator";
import { cleanTitle } from "../utils/cleaners";

interface XShareModalProps {
  leak: Leak | null;
  isOpen: boolean;
  onClose: () => void;
  isAdmin?: boolean;
}

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "/api";

export const XShareModal: React.FC<XShareModalProps> = ({
  leak,
  isOpen,
  onClose,
  isAdmin = false,
}) => {
  const [currentLeak, setCurrentLeak] = useState<Leak | null>(leak);
  const [draft, setDraft] = useState("");
  const [userEdited, setUserEdited] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [copied, setCopied] = useState(false);
  const [imageBlobUrl, setImageBlobUrl] = useState<string | null>(null);
  const [imageLoading, setImageLoading] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [zoomOpen, setZoomOpen] = useState(false);

  const modalRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Initialize draft and image state when leak changes or modal opens
  useEffect(() => {
    let active = true;
    if (isOpen && leak) {
      setCurrentLeak(leak);
      setDraft(formatXPost(leak));
      setUserEdited(false);
      setSelectedImageIndex(0);
      setCopied(false);
      setImageError(false);

      // Fetch freshly normalized leak document from API to ensure all backend normalized fields are available
      fetchLeakById(leak._id)
        .then((fresh) => {
          if (!active || !fresh) return;
          const merged = { ...leak, ...fresh };
          setCurrentLeak(merged);
          setDraft((prevDraft) => {
            // Only update draft if user hasn't started manually editing
            return userEdited ? prevDraft : formatXPost(merged);
          });
        })
        .catch((err) => {
          console.warn("Failed to fetch fresh leak details:", err);
        });
    }
    return () => {
      active = false;
    };
  }, [isOpen, leak?._id]);

  // Keyboard accessibility: ESC closes modal
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape" && isOpen) {
        if (zoomOpen) {
          setZoomOpen(false);
        } else {
          onClose();
        }
      }
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, zoomOpen, onClose]);

  // Extract available images for the leak
  const availableImages = React.useMemo(() => {
    if (!leak) return [];
    const list: string[] = [];
    if (leak.screenshot_path) list.push(leak.screenshot_path);
    if ((leak as any).screenshot && !list.includes((leak as any).screenshot)) {
      list.push((leak as any).screenshot);
    }
    const rawList = (leak as any).screenshots || (leak as any).all_images;
    if (Array.isArray(rawList)) {
      for (const item of rawList) {
        const p = typeof item === "string" ? item : item?.path;
        if (p && !list.includes(p)) list.push(p);
      }
    }
    return list;
  }, [leak]);

  const hasScreenshots = availableImages.length > 0;

  // Load watermarked image with admin authentication
  useEffect(() => {
    let active = true;
    if (!isOpen || !leak || !hasScreenshots) {
      setImageBlobUrl(null);
      setImageLoading(false);
      return;
    }

    setImageLoading(true);
    setImageError(false);

    const token = localStorage.getItem("access_token") || "";
    const fetchUrl = `${API_BASE_URL}/leaks/${leak._id}/watermarked-image?image_index=${selectedImageIndex}${token ? `&token=${encodeURIComponent(token)}` : ""}`;

    fetch(fetchUrl, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error(`Failed to load watermarked image: ${res.status}`);
        }
        return res.blob();
      })
      .then((blob) => {
        if (!active) return;
        const objectUrl = URL.createObjectURL(blob);
        setImageBlobUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return objectUrl;
        });
        setImageLoading(false);
      })
      .catch((err) => {
        if (!active) return;
        console.warn("Watermarked image fetch error:", err);
        setImageError(true);
        setImageLoading(false);
      });

    return () => {
      active = false;
    };
  }, [isOpen, leak, selectedImageIndex, hasScreenshots]);

  // Clean up object URL when modal unmounts
  useEffect(() => {
    return () => {
      if (imageBlobUrl) {
        URL.revokeObjectURL(imageBlobUrl);
      }
    };
  }, [imageBlobUrl]);

  if (!isOpen || !leak) return null;

  // Enforce admin permission check
  if (!isAdmin) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
        <div className="relative w-full max-w-md rounded-xl border border-red-500/30 bg-slate-900 p-6 text-center shadow-2xl">
          <AlertTriangle size={36} className="mx-auto mb-3 text-red-400" />
          <h3 className="text-lg font-bold text-white mb-2">Access Restricted</h3>
          <p className="text-sm text-slate-300 mb-5">
            Only authorized administrator accounts have permission to prepare and share intelligence alerts to X.
          </p>
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg bg-slate-800 text-slate-200 hover:bg-slate-700 text-sm font-medium transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  const validation = calculateXPostLength(draft);
  const activeLeak = currentLeak || leak;
  const forumClean = (activeLeak.forum || "").toLowerCase().trim();
  const isRansomware =
    activeLeak.forum_type === "ransomware" ||
    forumClean.includes("ransom") ||
    KNOWN_RANSOMWARE_GROUPS.some((g) => forumClean.includes(g));

  const threatActor = getVerifiedThreatActor(activeLeak);
  const organization =
    activeLeak.company_name ||
    (activeLeak as any).company ||
    activeLeak.victim ||
    (isRansomware ? (activeLeak.title || activeLeak.thread_title || activeLeak.thread_title_list) : null);

  const sector =
    activeLeak.primary_sector ||
    (activeLeak.sectors && activeLeak.sectors[0]) ||
    (activeLeak as any).category ||
    (activeLeak as any).industry ||
    activeLeak.sector ||
    null;

  let location =
    activeLeak.country ||
    (activeLeak.countries && activeLeak.countries[0]) ||
    activeLeak.location ||
    null;

  if (!location && (activeLeak as any).stock_ticker) {
    const ticker = String((activeLeak as any).stock_ticker);
    if (ticker.includes("NYSE:") || ticker.includes("NASDAQ:") || ticker.includes("AMEX:")) {
      location = "United States";
    }
  }

  const rawPubDate = activeLeak.published_at || activeLeak.published_date || activeLeak.created_at;
  const pubDate = rawPubDate
    ? new Date(rawPubDate).toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" })
    : "Unknown date";

  const sectorCountryDisplay = (() => {
    if (sector && location) return `${sector} (${location})`;
    return sector || location || "—";
  })();

  const handleCopyPost = async () => {
    if (!draft.trim()) {
      toast.error("Draft is empty");
      return;
    }
    try {
      await navigator.clipboard.writeText(draft);
      setCopied(true);
      toast.success("Post text copied to clipboard");
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Failed to copy post text to clipboard");
    }
  };

  const handleDownloadWatermarkedImage = () => {
    if (!imageBlobUrl) {
      toast.error("No watermarked image available to download");
      return;
    }
    const a = document.createElement("a");
    a.href = imageBlobUrl;
    a.download = `threatactix_leak_${leak._id.slice(-8)}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    toast.success("Watermarked evidence image downloaded", {
      description: "Attach this image to your X post in the composer window.",
    });
  };

  const handleContinueToX = () => {
    if (validation.isEmpty) {
      toast.error("Draft post cannot be empty");
      return;
    }
    if (validation.isOverLimit) {
      toast.error(`Draft exceeds X's limit by ${Math.abs(validation.remaining)} characters`, {
        description: "Please shorten the text before continuing to X.",
      });
      return;
    }

    // Safely encode text for X intent
    const xIntentUrl = `https://x.com/intent/post?text=${encodeURIComponent(draft)}`;
    window.open(xIntentUrl, "_blank", "noopener,noreferrer");

    toast.info("Opening X / Twitter post composer", {
      description: hasScreenshots && !imageError
        ? "Remember to attach your downloaded watermarked image in the composer."
        : "Complete your post in the opened X tab.",
    });
  };

  const handleResetDraft = () => {
    setUserEdited(false);
    setDraft(formatXPost(activeLeak));
    toast.info("Draft reset to default structured format");
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-labelledby="prepare-x-post-title"
    >
      <div
        ref={modalRef}
        className="relative w-full max-w-4xl max-h-[92vh] flex flex-col rounded-2xl border border-slate-800 bg-[#121214] shadow-2xl overflow-hidden my-auto"
      >
        {/* ── HEADER ───────────────────────────────────────────── */}
        <div className="flex items-center justify-between border-b border-slate-800/80 px-6 py-4 bg-slate-900/40">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-black border border-slate-700/80 flex items-center justify-center text-white font-bold text-sm shadow-inner">
              𝕏
            </div>
            <div>
              <h2 id="prepare-x-post-title" className="text-[17px] font-semibold text-white tracking-tight">
                Prepare X Post
              </h2>
              <p className="text-xs text-slate-400">
                Review verified leak intelligence and evidence before opening composer
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
            aria-label="Close dialog"
          >
            <X size={18} />
          </button>
        </div>

        {/* ── BODY (Scrollable) ─────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Metadata Overview Strip */}
          <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-4 space-y-2.5">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider block mb-1">
                  Original Title
                </span>
                <p className="text-sm font-medium text-slate-200 line-clamp-2">
                  {cleanTitle(leak.title || leak.thread_title || leak.thread_title_list || "Untitled")}
                </p>
              </div>
              <div className="text-right whitespace-nowrap">
                <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider block mb-1">
                  Source Platform
                </span>
                <span className="inline-block px-2.5 py-0.5 rounded-md bg-slate-800 border border-slate-700 text-xs font-medium text-slate-300">
                  {leak.forum || "Unknown"}
                </span>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-2 border-t border-slate-800/60 text-xs">
              <div>
                <span className="text-slate-500 block text-[10px] font-semibold uppercase">Threat Actor</span>
                <span className="text-slate-300 font-medium truncate block">{threatActor || "—"}</span>
              </div>
              <div>
                <span className="text-slate-500 block text-[10px] font-semibold uppercase">Organization</span>
                <span className="text-slate-300 font-medium truncate block">{organization || "—"}</span>
              </div>
              <div>
                <span className="text-slate-500 block text-[10px] font-semibold uppercase">Sector / Country</span>
                <span className="text-slate-300 font-medium truncate block" title={sectorCountryDisplay}>
                  {sectorCountryDisplay}
                </span>
              </div>
              <div>
                <span className="text-slate-500 block text-[10px] font-semibold uppercase">Published Date</span>
                <span className="text-slate-300 font-medium truncate block">{pubDate}</span>
              </div>
            </div>
          </div>

          {/* Post Draft & Editor Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Left Column: Post Draft Textarea (7 cols) */}
            <div className="lg:col-span-7 flex flex-col space-y-2">
              <div className="flex items-center justify-between">
                <label htmlFor="x-post-draft-textarea" className="text-xs font-bold text-slate-300 uppercase tracking-wider">
                  Post Content
                </label>
                <button
                  onClick={handleResetDraft}
                  className="flex items-center gap-1 text-[11px] text-slate-400 hover:text-cyan-400 transition-colors"
                  title="Reset to original generated text"
                >
                  <RefreshCw size={11} /> Reset draft
                </button>
              </div>

              <div className="relative">
                <textarea
                  id="x-post-draft-textarea"
                  ref={textareaRef}
                  value={draft}
                  onChange={(e) => {
                    setUserEdited(true);
                    setDraft(e.target.value);
                  }}
                  rows={10}
                  className={`w-full rounded-xl border bg-slate-950 p-3.5 text-sm font-sans leading-relaxed text-slate-200 placeholder-slate-600 focus:outline-none transition-colors resize-none ${
                    validation.isOverLimit
                      ? "border-red-500/80 focus:border-red-500"
                      : "border-slate-800 focus:border-cyan-500/60"
                  }`}
                  placeholder="Draft your post for X..."
                />
              </div>

              {/* Character Limit and Counter */}
              <div className="flex items-center justify-between pt-1">
                <div className="flex items-center gap-2">
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-mono font-medium ${
                      validation.isOverLimit
                        ? "bg-red-500/15 text-red-400 border border-red-500/30"
                        : validation.remaining <= 20
                        ? "bg-amber-500/15 text-amber-400 border border-amber-500/30"
                        : "bg-slate-800 text-slate-300 border border-slate-700/60"
                    }`}
                  >
                    {validation.weightedLength} / {X_POST_MAX_LIMIT}
                  </span>
                  {validation.urlCount > 0 && (
                    <span className="text-[11px] text-slate-500">
                      ({validation.urlCount} URL{validation.urlCount > 1 ? "s" : ""} counted as 23 chars)
                    </span>
                  )}
                </div>

                {validation.isOverLimit && (
                  <span className="text-xs text-red-400 font-medium">
                    Exceeds limit by {Math.abs(validation.remaining)} characters
                  </span>
                )}
              </div>

              {validation.isOverLimit && (
                <div className="rounded-lg bg-red-950/40 border border-red-500/30 p-2.5 text-xs text-red-300 flex items-start gap-2">
                  <AlertTriangle size={14} className="mt-0.5 text-red-400 shrink-0" />
                  <span>
                    X allows a maximum of 280 characters for standard posts. Please trim the draft before continuing to X.
                  </span>
                </div>
              )}
            </div>

            {/* Right Column: Evidence Screenshot Preview & Watermark (5 cols) */}
            <div className="lg:col-span-5 flex flex-col space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-slate-300 uppercase tracking-wider flex items-center gap-1.5">
                  <ImageIcon size={13} className="text-cyan-400" />
                  Evidence Screenshot
                </span>
                {availableImages.length > 1 && (
                  <span className="text-[11px] text-slate-400">
                    {availableImages.length} images available
                  </span>
                )}
              </div>

              {/* Multiple images thumbnail selector */}
              {availableImages.length > 1 && (
                <div className="flex items-center gap-1.5 pb-1">
                  {availableImages.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setSelectedImageIndex(idx)}
                      className={`px-2 py-0.5 rounded text-[11px] font-medium transition-colors ${
                        selectedImageIndex === idx
                          ? "bg-cyan-500 text-slate-950 font-bold"
                          : "bg-slate-800 text-slate-400 hover:text-white"
                      }`}
                    >
                      Image {idx + 1}
                    </button>
                  ))}
                </div>
              )}

              {/* Image Preview Box */}
              <div className="relative rounded-xl border border-slate-800 bg-slate-950 overflow-hidden min-h-[250px] max-h-[300px] flex items-center justify-center group">
                {imageLoading && (
                  <div className="flex flex-col items-center justify-center p-8 text-center text-slate-400 space-y-2">
                    <RefreshCw size={24} className="animate-spin text-cyan-400" />
                    <span className="text-xs">Applying Threatactix watermark...</span>
                  </div>
                )}

                {!imageLoading && hasScreenshots && imageBlobUrl && !imageError && (
                  <div className="relative w-full h-full flex items-center justify-center bg-black/75 p-2">
                    <img
                      src={imageBlobUrl}
                      alt="Watermarked Evidence"
                      className="max-h-[250px] w-auto max-w-full object-contain cursor-pointer transition-transform group-hover:scale-[1.01] rounded shadow-lg"
                      onClick={() => setZoomOpen(true)}
                    />
                    <button
                      onClick={() => setZoomOpen(true)}
                      className="absolute bottom-2.5 right-2.5 px-2 py-1 rounded-lg bg-black/80 hover:bg-black text-white/90 hover:text-white border border-slate-700 text-xs flex items-center gap-1.5 shadow-xl transition-colors backdrop-blur-sm"
                      title="Click to enlarge"
                    >
                      <ZoomIn size={13} className="text-cyan-400" />
                      <span className="text-[11px] font-medium">Expand</span>
                    </button>
                  </div>
                )}

                {!imageLoading && (!hasScreenshots || imageError) && (
                  <div className="flex flex-col items-center justify-center p-6 text-center text-slate-500 space-y-2">
                    <ImageIcon size={32} className="text-slate-600 stroke-[1.5]" />
                    <p className="text-xs text-slate-400 font-medium">
                      {imageError ? "Evidence screenshot could not be loaded." : "No evidence screenshot attached."}
                    </p>
                    <p className="text-[11px] text-slate-600 max-w-[200px]">
                      A text-only intelligence post will be prepared for X.
                    </p>
                  </div>
                )}
              </div>

              {/* Watermark Download & Instructions */}
              {hasScreenshots && imageBlobUrl && !imageError && (
                <div className="space-y-2">
                  <button
                    onClick={handleDownloadWatermarkedImage}
                    className="w-full py-2 px-3 rounded-lg bg-slate-800/90 hover:bg-slate-700/90 border border-slate-700 text-xs font-semibold text-slate-200 flex items-center justify-center gap-1.5 transition-colors shadow-sm"
                  >
                    <Download size={13} className="text-cyan-400" />
                    Download Watermarked Image
                  </button>

                  <div className="rounded-lg bg-indigo-950/20 border border-indigo-500/20 p-2.5 text-[11px] text-slate-300 leading-relaxed">
                    <span className="font-semibold text-indigo-300 block mb-0.5">ℹ️ Manual image attachment required</span>
                    The browser X composer does not support attaching files through URL parameters. Download the watermarked image above and drag or upload it in the composer window.
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── FOOTER ACTIONS ───────────────────────────────────── */}
        <div className="border-t border-slate-800/80 px-6 py-4 bg-slate-900/60 flex items-center justify-between gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm font-medium text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
          >
            Cancel
          </button>

          <div className="flex items-center gap-3">
            <button
              onClick={handleCopyPost}
              className="px-4 py-2 rounded-lg text-sm font-medium border border-slate-700 bg-slate-800 text-slate-200 hover:bg-slate-700 hover:text-white flex items-center gap-1.5 transition-colors"
            >
              {copied ? <Check size={14} className="text-emerald-400" /> : <Copy size={14} />}
              {copied ? "Copied" : "Copy Post"}
            </button>

            <button
              onClick={handleContinueToX}
              disabled={validation.isOverLimit || validation.isEmpty}
              className={`px-5 py-2 rounded-lg text-sm font-semibold flex items-center gap-2 shadow-lg transition-all ${
                validation.isOverLimit || validation.isEmpty
                  ? "bg-slate-800 text-slate-500 border border-slate-700 cursor-not-allowed opacity-60"
                  : "bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-slate-950 font-bold shadow-cyan-500/20"
              }`}
            >
              <span>Continue to 𝕏</span>
              <ExternalLink size={14} />
            </button>
          </div>
        </div>
      </div>

      {/* ── ENLARGE / ZOOM MODAL ─────────────────────────────── */}
      {zoomOpen && imageBlobUrl && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/95 p-4 cursor-pointer"
          onClick={() => setZoomOpen(false)}
        >
          <div className="relative max-w-5xl max-h-[90vh] overflow-auto rounded-xl border border-slate-700">
            <img src={imageBlobUrl} alt="Watermarked Evidence Full" className="w-full h-auto object-contain" />
            <button
              onClick={() => setZoomOpen(false)}
              className="absolute top-3 right-3 p-1.5 rounded-full bg-black/80 text-white hover:bg-black border border-slate-600"
            >
              <X size={18} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default XShareModal;
